import * as React from 'react'

import { useMutation } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  NotificationsNotifierScopesDocument,
  NotificationsUpdateNotifierScopeDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'

import { PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import userPreferences from 'modules/bookkeepers/store/collections/userPreferences'
import { PreferencesList } from 'modules/notifications/components/Preferences/PreferencesList'
import { PreferencesSearchBar } from 'modules/notifications/components/Preferences/PreferencesSearchBar'
import notifiers from 'modules/notifications/store/collections/notifiers'
import type Notifier from 'modules/notifications/store/models/Notifier'
import type UserPreference from 'modules/notifications/store/models/UserPreference'

export const UserPreferences = observer(() => {
  const isBookkeeperView = useBookkeeperView()
  const { loading: loadingScopes, data } = useQuery(NotificationsNotifierScopesDocument, {
    context: {
      isBookkeeperView,
    },
  })
  const [updateScope] = useMutation(NotificationsUpdateNotifierScopeDocument, {
    refetchQueries: [{ query: NotificationsNotifierScopesDocument }],
    context: {
      isBookkeeperView,
    },
  })
  const { loading } = useAsyncData(
    () =>
      Promise.all([
        notifiers.fetch({
          data: {
            bookkeeper_view: true,
          },
        }),
        userPreferences.fetch({
          data: {
            bookkeeper_view: true,
          },
        }),
      ]),
    []
  )
  const [search, setSearch] = React.useState('')

  const handlePreferenceUpdated = async (preference: UserPreference, newValue: boolean) => {
    const { success } = await asyncAction(() => preference.updateValue(newValue))

    if (success) {
      track(trackingEvents.bookkeeper.settings.changePersonalNotification, {
        status: newValue,
        application: PROFESSIONALS_APP,
        value_name: preference.notifier,
        channel: preference.channel,
      })
    }
  }

  const handleScopeUpdated = async (notifier: string, newValue: string) => {
    await updateScope({
      variables: {
        notifier,
        value: newValue,
      },
    })
  }

  const filteredNotifiers = () =>
    notifiers.models.filter((notifier: Notifier) =>
      ['payroll_incidences_status', 'payroll_incidences_message'].includes(notifier.category)
    )

  if (loading || loadingScopes) {
    return (
      <Box width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Box width="full" paddingX="s48" paddingY="s32">
        <PreferencesSearchBar search={search} onSearch={setSearch} />
      </Box>
      <PreferencesList
        notifiers={filteredNotifiers()}
        preferences={userPreferences.models}
        notifierScopes={data?.notifications.accessNotifierScopes || []}
        search={search}
        onPreferenceUpdated={handlePreferenceUpdated}
        onScopeUpdated={handleScopeUpdated}
      />
    </>
  )
})
